import type { Fraction } from '../util/fractionType';

export interface CRF {
  type: 'crf',
  crf: number,
  maxBitrate?: number, // Constrained CRF
}

export interface VBR {
  type: 'vbr',
  bitrate: number,
  maxBitrate: number,
}

export interface CBR {
  type: 'cbr',
  bitrate: number,
}

export type MediaType = 'video' | 'audio';

export type FieldOrder = 'progressive' | 'top_field_first' | 'bottom_field_first';

export interface MpegTs {
  name: 'mpegts'
}

export interface MpJpeg {
  name: 'mpjpeg',
}

export interface Isom {
  faststart: boolean,
  timescale?: number,
  brand?: string,
}

export interface Mp4 extends Isom {
  name: 'mp4',
}

export interface M4a extends Isom {
  name: 'm4a',
}

export interface Mov extends Isom {
  name: 'mov',
}

export function isomFormat(format: Format): Isom | null {
  switch (format.name) {
    case 'mp4':
    case 'mov':
      return format;
    default:
      return null;
  }
}

export interface WebM {
  name: 'webm',
}

export interface Wav {
  name: 'wav',
}

export interface Mxf {
  name: 'mxf',
}

export interface Nut {
  name: 'nut',
}

export interface Fifo {
  name: 'fifo',
  inner: Format,
  queueSize: number,
  dropOnOverflow: boolean,
  recoverOnError: 'no' | 'retryable' | 'all',
}

export interface Raw {
  name: 'raw',
}

export type Format = MpegTs | Mp4 | M4a | Mov | WebM | Wav | Mxf | Nut | Fifo | MpJpeg | Raw;

export type SegmentFormat = MpegTs | Mp4;

export interface Jpg {
  name: 'jpg',
}

export interface Png {
  name: 'png',
}

export type ImageFormat = Jpg | Png;

export type PixelFormat =
  'yuv420p' |
  'yuv422p' |
  'yuyv422' |
  'uyvy422' |
  'rgba' |
  'rgb24' |
  'bgr0' |
  'yuv422p10le' |
  'yuv420p10le' |
  'yuv444p10le' |
  'yuv422p12le' |
  'yuva444p12le' |
  'yuvj420p' |
  'yuvj422p' |
  'nv12';

export function pixelFormatToBitDepth(pixelFormat: PixelFormat): number {
  switch (pixelFormat) {
    case 'yuv420p':
    case 'yuvj420p':
    case 'yuv422p':
    case 'yuvj422p':
    case 'yuyv422':
    case 'uyvy422':
    case 'rgba':
    case 'rgb24':
    case 'bgr0':
    case 'nv12':
      return 8;
    case 'yuv420p10le':
    case 'yuv422p10le':
    case 'yuv444p10le':
      return 10;
    case 'yuv422p12le':
    case 'yuva444p12le':
      return 12;
  }
}

export function pixelFormatToBitsPerPixel(pixelFormat: PixelFormat): number {
  // Note about chrome subsampling:
  //  420:  4 pixels (2x2) => 4y + 1u + 1v => 6 / 4
  //  422:  2 pixels (2x1) => 2y + 1u + 1v => 4 / 2
  //  444:  1 pixel (1x1)  => 1y + 1u + 1v => 3
  switch (pixelFormat) {
    case 'yuv420p':
    case 'yuvj420p': return (8 * 6) / 4;
    case 'yuv422p':
    case 'yuvj422p':
    case 'yuyv422':
    case 'uyvy422': return (8 * 4) / 2;
    case 'rgba': return (8 * 4);
    case 'rgb24': return (8 * 3);
    case 'bgr0': return (8 * 4);
    // Note that 10-bit and 12-bit are actually represented as
    // 16-bit values where only the lower bits are used and the
    // rest is padding for alignment purposes.
    case 'yuv420p10le': return (16 * 6) / 4;
    case 'yuv422p10le': return (16 * 4) / 2;
    case 'yuv444p10le': return 16 * 3;
    case 'yuv422p12le': return (16 * 4) / 2
    case 'yuva444p12le': return 16 * 4;
    case 'nv12': return 12;
  }
}

export function stringToPixelFormat(s: string): PixelFormat | null {
  switch (s) {
    case 'yuv420p':
    case 'yuv422p':
    case 'yuyv422':
    case 'uyvy422':
    case 'rgba':
    case 'rgb24':
    case 'yuv422p10le':
    case 'yuv420p10le':
    case 'yuv444p10le':
    case 'yuva444p12le':
      return s;
    default:
      return null;
  }
}

export function pixelFormatHasAlpha(pixelFormat: PixelFormat): boolean {
  switch (pixelFormat) {
    case 'yuv420p':
    case 'yuv422p':
    case 'yuyv422':
    case 'uyvy422':
    case 'rgb24':
    case 'yuv422p10le':
    case 'yuv420p10le':
    case 'yuv444p10le':
    case 'yuvj420p':
    case 'yuvj422p':
    case 'nv12':
      return false;
    case 'yuv422p12le':
    case 'yuva444p12le':
    case 'rgba':
    case 'bgr0':
      return true;
  }
}

export type ColorSpaceSpace = 'bt470m' | 'bt470bg' | 'bt601-6-525' | 'bt601-6-625' | 'bt709' | 'smpte170m' | 'smpte240m' | 'bt2020' | 'srgb';
export type ColorSpaceRange = 'full' | 'limited';

export interface ColorSpace {
  /**
   *  This is a setting that affects space, transfer characteristics and
   *  primaries which are all part of the definition of a color space
   */
  space: ColorSpaceSpace,

  /**
   *  Color range is not part of the color space definitions as this maps how
   *  to convert the samples of a pixel format to the normalized input values
   *  to the color space calculations.
   */
  range: ColorSpaceRange,
}

export function stringToColorSpaceSpace(s: string | undefined): ColorSpaceSpace | null {
  switch (s) {
    case 'bt470m':
    case 'bt470bg':
    case 'bt601-6-525':
    case 'bt601-6-625':
    case 'bt709':
    case 'smpte170m':
    case 'smpte240m':
    case 'bt2020':
      return s;
    default:
      return null;
  }
}

export type ChannelLayout = 'mono' |'stereo' | '3.0' | '4.0' | '5.0' | 'hexagonal' | '5.1' | '7.0' | 'octagonal' | '7.1';

export type ScaleMode = 'pad' | 'crop';

export const RESOLUTION_DEFAULT_SAR : Fraction = { numerator: 1, denominator: 1 };

export interface Resolution {
  width: number,
  height: number,
  /**
   *  Sample/Pixel aspect ratio. When omitted it implies the default value of 1:1
   */
  sar?: Fraction,
}

/**
 *  Utility type that must specify SAR which is compatible with both Resolution
 *  and ResolutionInfo.
 */
export interface StrictResolution {
  width: number,
  height: number,
  sar: Fraction,
}

export function filenameExtensionForFormat(format: Format | ImageFormat): string {
  switch (format.name) {
    case 'mpegts': return '.ts';
    case 'mp4': return '.mp4';
    case 'm4a': return '.m4a';
    case 'mov': return '.mov';
    case 'webm': return '.webm';
    case 'wav': return '.wav';
    case 'mxf': return '.mxf';
    case 'nut': return '.nut';
    case 'jpg': return '.jpg';
    case 'png': return '.png';
    case 'fifo': return filenameExtensionForFormat(format.inner);
    case 'mpjpeg': return '.mjpg';
    case 'raw': return '.raw';
  }
}

export function mimeTypeForFormat(format: Format | ImageFormat) : string {
  switch (format.name) {
    case 'mpegts': return 'video/mp2t';
    case 'mp4': return 'video/mp4';
    case 'm4a': return 'audio/mp4';
    case 'mov': return 'video/quicktime';
    case 'webm': return 'video/webm';
    case 'wav': return 'audio/x-wav';
    case 'mxf': return 'application/mxf';
    case 'nut': return 'video/x-nut';
    case 'jpg': return 'image/jpeg';
    case 'png': return 'image/png';
    case 'fifo': return mimeTypeForFormat(format);
    case 'mpjpeg': return 'image/jpeg';

    // Technically this depends on the codec being used, but this is only
    // used for test purposes at this point so it really doesn't matter.
    case 'raw': return 'application/octet-stream';
  }
}

export function defaultChannelLayout(channelCount: number): ChannelLayout {
  switch (channelCount) {
    case 1: return 'mono';
    case 2: return 'stereo';
    case 3: return '3.0';
    case 4: return '4.0';
    case 5: return '5.0';
    case 6: return '5.1';
    case 7: return '7.0';
    case 8: return '7.1';
    default: throw Error(`No default layout defined for channel count ${channelCount}`);
  }
}

export function channelLayoutToChannelCount(layout: ChannelLayout): number {
  switch (layout) {
    case 'mono': return 1;
    case 'stereo': return 2;
    case '3.0': return 3;
    case '4.0': return 4;
    case '5.0': return 5;
    case '5.1':
    case 'hexagonal': return 6;
    case '7.0': return 7;
    case '7.1':
    case 'octagonal': return 8;
  }
}

export interface InputSettings {
  throttleToRealTime?: boolean,
  probeSize?: number,
  seekable?: boolean,
  // The number of extra seconds to seek backwards and
  // forwards from what is required. Select filters will
  // trim what is extra. (default: 1)
  seekMargin?: number;

  // Set to true to enable reconnect for protocols that support it (http etc.)
  reconnect?: boolean,

  // Enable timeout (in ms) for protocols that support it (http etc.)
  timeoutMs?: number,
}

export function parseFieldOrder(fieldOrderString: string): FieldOrder | undefined {
  if (
    (fieldOrderString === 'progressive') ||
    (fieldOrderString === 'top_field_first') ||
    (fieldOrderString === 'bottom_field_first')
  ) {
    return fieldOrderString;
  }
  return undefined;
}


